import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {FiFlag, FiHash, FiPlay} from "react-icons/fi";

import {TourMembership} from "../../models/membership";
import {formatDateTime, isNowInRange} from "../../utils/formatDateTime";
import TourDialog from "../dialogs/TourDialog";
import TourUpdateForm from "../forms/tour/TourUpdateForm";
import {QUERY_PARAMS} from "../../constants/queryParams";
import {NAV} from "../../constants/navigation";
import {useTourDeletion} from "../../hooks/useTourDeletion";
import {UserRole} from "../../models/account";
import {RoleIcon} from "../../utils/RoleIcon";
import QrCodeDialog from "../dialogs/QrCodeDialog";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import OptionDialog from "../dialogs/OptionDialog";

type TourCardProps = {
    membership: TourMembership;
    onEditMembership: (membership: TourMembership) => void;
    onEditTour: (membership: TourMembership) => void;
    onReloadMemberships: () => void;
    onSaved: () => void;
};

export default function TourCard({
                                     membership,
                                     onEditMembership,
                                     onEditTour,
                                     onReloadMemberships,
                                     onSaved
                                 }: TourCardProps) {
    const {t} = useTranslation();

    const [showQr, setShowQr] = useState(false);
    const [showEditTourDialog, setShowEditTourDialog] = useState(false);
    const [isActiveNow, setIsActiveNow] = useState(
        isNowInRange(membership.tour.validFrom, membership.tour.validTo)
    );

    const {
        openDeleteTourConfirmDialog,
        openLeaveConfirmDialog,
        openDeleteOptionForAdmin,
        otherAdmins,
        membershipsInTour,
        setOpenDeleteTourConfirmDialog,
        setOpenLeaveConfirmDialog,
        setOpenDeleteOptionForAdmin,
        handleDelete,
        handleDeleteTour,
        handleLeaveTour,
        loadMemberships
    } = useTourDeletion(membership, onReloadMemberships);

    const isApplied = membership.role === UserRole.APPLIED;
    const isActive = isNowInRange(membership.tour.validFrom, membership.tour.validTo);

    const appliedCount =
        membershipsInTour?.memberships.filter(m => m.userRole === UserRole.APPLIED).length ?? 0;

    const isAdminOrCreator = membership.role === UserRole.ADMIN || membership.role === UserRole.CREATOR;

    useEffect(() => {
        loadMemberships();
    }, []);

    useEffect(() => {
        const start = new Date(membership.tour.validFrom);
        const end = new Date(membership.tour.validTo);
        const now = new Date();

        // Zustand sofort setzen
        setIsActiveNow(isNowInRange(membership.tour.validFrom, membership.tour.validTo));

        // Nächsten Wechselpunkt bestimmen
        let nextChange: Date | null = null;

        if (now < start) {
            nextChange = start;
        } else if (now >= start && now <= end) {
            nextChange = end;
        } else {
            nextChange = null;
        }

        if (nextChange) {
            const delay = nextChange.getTime() - now.getTime();

            const timer = setTimeout(() => {
                setIsActiveNow(
                    isNowInRange(membership.tour.validFrom, membership.tour.validTo)
                );
            }, delay);

            return () => clearTimeout(timer);
        }
    }, [membership.tour.validFrom, membership.tour.validTo]);

    function handleEditTourClick() {
        onEditTour(membership);
        setShowEditTourDialog(true);
    }

    return (
        <div className={`contact-card ${isApplied ? "applied" : ""}`}>
            <div className="contact-content">
                <div className="contact-header">
                    {isActive && <span className="green-dot" title={t("tours.tooltip.tourActive")}/>}
                    <strong>{membership.tour.destination}</strong>

                    <div className="contact-lock-icon">
                        <RoleIcon role={membership.role}/>
                    </div>
                </div>

                <div className="info-section">
                    <div className="info-row">
                        <span className="info-label">
                            <FiPlay title={t("tours.fields.validFrom")} className="info-icon"/>
                        </span>
                        <span className="info-value">
                            {formatDateTime(membership.tour.validFrom)}
                        </span>
                    </div>

                    <div className="info-row">
                        <span className="info-label">
                            <FiFlag title={t("tours.fields.tours.fields.validTo")} className="info-icon"/>
                        </span>
                        <span className="info-value">
                            {formatDateTime(membership.tour.validTo)}
                        </span>
                    </div>

                    <div className="info-row">
                        <span className="info-label">
                            <FiHash title={t("tours.fields.tourCode")} className="info-icon"/>
                        </span>
                        <span className="info-value code">
                            {membership.tour.tourCode}
                        </span>
                    </div>
                </div>
            </div>

            <div className="contact-actions">
                <div className="button-with-badge">
                    <button
                        type="button"
                        className="form-button"
                        onClick={handleEditTourClick}
                    >
                        {t("tours.buttons.editTour")}
                    </button>

                    {appliedCount > 0 && isAdminOrCreator && (
                        <div className="button-badge">{appliedCount}</div>
                    )}
                </div>

                <button
                    type="button"
                    className="form-button-secondary contact-button"
                    onClick={() => onEditMembership(membership)}
                >
                    {t("tours.buttons.editMembership")}
                </button>

                <button
                    type="button"
                    className="form-button-secondary contact-button"
                    onClick={handleDelete}
                >
                    {t("tours.buttons.deleteTour")}
                </button>

                {membership.role !== UserRole.APPLIED && (
                    <button
                        type="button"
                        className="form-button-secondary contact-button"
                        onClick={() => setShowQr(true)}
                    >
                        {t("tours.buttons.inviteToTour")}
                    </button>
                )}
            </div>

            <QrCodeDialog
                open={showQr}
                code={membership.tour.tourCode}
                codeType={QUERY_PARAMS.TOUR}
                qrDestinationPage={NAV.TOURS}
                onClose={() => setShowQr(false)}
            />

            <ConfirmationDialog
                open={openLeaveConfirmDialog}
                title="Tour verlassen"
                message="Möchtest du die Tour wirklich verlassen?"
                onConfirm={handleLeaveTour}
                onClose={() => setOpenLeaveConfirmDialog(false)}
            />

            <ConfirmationDialog
                open={openDeleteTourConfirmDialog}
                title="Tour löschen"
                message="Möchtest du die Tour wirklich löschen?"
                onConfirm={handleDeleteTour}
                onClose={() => setOpenDeleteTourConfirmDialog(false)}
            />

            <OptionDialog
                open={openDeleteOptionForAdmin}
                title="Tour löschen oder verlassen?"
                message="Möchtest du die Tour verlassen oder für alle löschen?"
                firstOptionLabel="Tour verlassen"
                secondOptionLabel="Tour löschen"
                disableFirstOption={!otherAdmins}
                tooltipFirstOption={!otherAdmins ? t("tours.dialog.cannotLeaveTour") : undefined}
                onFirstOption={() => setOpenLeaveConfirmDialog(true)}
                onSecondOption={() => setOpenDeleteTourConfirmDialog(true)}
                onClose={() => setOpenDeleteOptionForAdmin(false)}
            />

            {showEditTourDialog && (
                <TourDialog
                    tour={membership.tour}
                    membership={membership}
                    membershipsInTour={membershipsInTour!}
                    showOtherMembers={true}
                    onReloadMembershipsInTour={loadMemberships}
                    onClose={() => setShowEditTourDialog(false)}
                    onSaved={onSaved}
                    renderForm={({setMessage, onSaved}) => (
                        <TourUpdateForm
                            tour={membership.tour}
                            role={membership.role}
                            setMessage={setMessage}
                            onSaved={onSaved}
                        />
                    )}
                />
            )}
        </div>
    );
}

